import React, { useState } from "react";
import api from "./api";
import { projectSprintView } from "./sprintDesignModel";
import "./SprintDesigner.css";

function SprintDesigner({ repo, design: initial, mutateAllowed, onBack, onCommitted, onOpenTask }) {
  const [design, setDesign] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [ack, setAck] = useState(false);
  const [ackReason, setAckReason] = useState("");

  const view = projectSprintView(design);

  const save = async (body) => {
    const response = await api.updateSprintDesign(repo._id, design.sprintId, design.version, {
      expectedRevision: design.revision,
      ...body,
    });
    setDesign(response.data.sprintDesign);
    return response.data.sprintDesign;
  };

  const lock = async (featurePlanId, kind) => {
    try {
      setSaving(true);
      await save({
        candidatePatches: [{ featurePlanId, lockedDecision: { kind }, requiredThisSprint: kind === "include" }],
      });
    } catch (err) {
      setError(err.response?.data?.error || "Could not lock that choice.");
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    try {
      setSaving(true);
      const current = await save({});
      const response = await api.recommendSprintDesign(repo._id, current.sprintId, current.version, {
        expectedRevision: current.revision,
        idempotencyKey: `regen-${current.revision}`,
      });
      setDesign(response.data.sprintDesign);
    } catch (err) {
      setError(err.response?.data?.error || "Could not refresh the recommendation.");
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    try {
      setSaving(true);
      const response = await api.previewSprintCommit(repo._id, design.sprintId, design.version, {});
      setPreview(response.data.preview || []);
    } catch (err) {
      setError(err.response?.data?.error || "Could not preview Roadmap changes.");
    } finally {
      setSaving(false);
    }
  };

  const commit = async () => {
    try {
      setSaving(true);
      setError("");
      const response = await api.commitSprintDesign(repo._id, design.sprintId, design.version, {
        expectedRevision: design.revision,
        expectedContentFingerprint: design.contentFingerprint,
        idempotencyKey: `commit-${design.sprintId}-${design.version}`,
        capacityExceededAcknowledged: ack,
        capacityExceededReason: ackReason,
      });
      onCommitted(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Could not add this sprint to the Roadmap.");
    } finally {
      setSaving(false);
    }
  };

  if (!view) return null;

  return (
    <section className="sprint-designer" data-testid="sprint-designer">
      <div className="sprint-designer-header">
        <button type="button" className="secondary-button" onClick={onBack}>
          Back
        </button>
        <h3>Build this sprint</h3>
      </div>
      {error ? (
        <div className="error-message" role="alert">
          {error}
        </div>
      ) : null}

      <label>
        Sprint name
        <input
          data-testid="sprint-title"
          value={design.title || ""}
          disabled={!mutateAllowed || view.immutable}
          onChange={(e) => setDesign({ ...design, title: e.target.value })}
          onBlur={() => save({ title: design.title })}
        />
      </label>
      <label>
        What should this sprint accomplish?
        <textarea
          data-testid="sprint-goal"
          value={design.sprintGoal || ""}
          disabled={!mutateAllowed || view.immutable}
          onChange={(e) => setDesign({ ...design, sprintGoal: e.target.value })}
          onBlur={() => save({ sprintGoal: design.sprintGoal })}
        />
      </label>
      <label>
        Start date (optional)
        <input
          type="date"
          value={design.startDate || ""}
          disabled={!mutateAllowed || view.immutable}
          onChange={(e) => setDesign({ ...design, startDate: e.target.value || null })}
          onBlur={() => save({ startDate: design.startDate || null })}
        />
      </label>
      <label>
        End date (optional)
        <input
          type="date"
          value={design.endDate || ""}
          disabled={!mutateAllowed || view.immutable}
          onChange={(e) => setDesign({ ...design, endDate: e.target.value || null })}
          onBlur={() => save({ endDate: design.endDate || null })}
        />
      </label>
      <fieldset>
        <legend>Capacity (optional)</legend>
        <p>Capacity is what you typed, not an AI fact. {view.capacityMessage}</p>
        <label>
          Amount
          <input
            type="number"
            min="0"
            data-testid="sprint-capacity-amount"
            disabled={!mutateAllowed}
            value={design.capacity?.amount ?? ""}
            onChange={(e) =>
              setDesign({
                ...design,
                capacity: { amount: Number(e.target.value), unit: design.capacity?.unit || "items" },
              })
            }
            onBlur={() => save({ capacity: design.capacity })}
          />
        </label>
      </fieldset>

      <h4>Included</h4>
      {view.include.map((row) => (
        <article key={row.candidate?.featurePlanId} data-testid={`designer-include-${row.candidate?.featurePlanId}`}>
          <strong>{row.candidate?.title}</strong>
          <p>{row.reason}</p>
          {mutateAllowed ? (
            <button type="button" className="secondary-button" onClick={() => lock(row.candidate.featurePlanId, "include")}>
              Lock my choice
            </button>
          ) : null}
          {onOpenTask ? (
            <button type="button" className="secondary-button" onClick={() => onOpenTask(row.candidate.featurePlanId)}>
              Open Task
            </button>
          ) : null}
        </article>
      ))}

      <h4>Waiting</h4>
      {view.wait.map((row) => (
        <article key={row.candidate?.featurePlanId}>
          <strong>{row.candidate?.title}</strong>
          <p>{row.reason}</p>
        </article>
      ))}

      {view.overlaps.some((item) => item.kind === "exact_file") ? (
        <p role="status">Some included Tasks touch the same files. That is confirmed overlap.</p>
      ) : null}
      {view.overlaps.some((item) => item.kind === "unknown") ? (
        <p role="status">Some Tasks have no change-area evidence, so overlap is unknown — not independent.</p>
      ) : null}

      <label>
        What done means
        <textarea
          data-testid="sprint-done"
          value={design.success?.statement || ""}
          disabled={!mutateAllowed}
          onChange={(e) =>
            setDesign({ ...design, success: { ...(design.success || {}), statement: e.target.value, checks: design.success?.checks || [] } })
          }
          onBlur={() => save({ success: design.success })}
        />
      </label>

      {view.recommendationFallback ? <p role="status">{view.fallbackReason}</p> : null}

      <div className="sprint-actions">
        <button type="button" className="secondary-button" data-testid="sprint-regenerate" disabled={!mutateAllowed || saving} onClick={regenerate}>
          Ask again, keep locked choices
        </button>
        <button type="button" className="secondary-button" data-testid="sprint-preview" disabled={!mutateAllowed || saving} onClick={loadPreview}>
          Preview Roadmap changes
        </button>
      </div>

      {preview ? (
        <section data-testid="sprint-preview-list">
          <h4>Roadmap changes for version {design.version}</h4>
          <ul>
            {preview.map((change) => (
              <li key={change.featurePlanId}>
                {change.action}: {change.featurePlanId.slice(-6)} → {change.toLane}
              </li>
            ))}
          </ul>
          <label>
            <input
              type="checkbox"
              data-testid="sprint-capacity-ack"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
            />
            I understand if this set is larger than my capacity.
          </label>
          <label>
            Why proceed anyway
            <input data-testid="sprint-capacity-reason" value={ackReason} onChange={(e) => setAckReason(e.target.value)} />
          </label>
          <button type="button" className="primary-button" data-testid="sprint-commit" disabled={!mutateAllowed || saving} onClick={commit}>
            Add sprint to Roadmap
          </button>
        </section>
      ) : null}
    </section>
  );
}

export default SprintDesigner;
